import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useConnectionState, useRoomContext } from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import handleAxiosError from "@/custolink-logic/api/handleAxiosError";
import {
  MAX_FRAME_BYTES,
  TOPICS,
  acceptSender,
  decodeEnvelope,
  encodeEnvelope,
  roleOfIdentity,
  roomStatePayloadSchema,
  type OpMessagePayload,
  type PointerPayload,
  type RoomStatePayload,
} from "./envelope";
import { initialSyncState, newUuid, syncReducer, type SyncState } from "./syncReducer";
import { renderFrame, type OpLogState } from "./annotations/opLog";
import type { OpDraft, Shape, SyncOp } from "./annotations/shapes";
import { createPointerThrottle, visiblePointers, type RemotePointer } from "./pointer/pointerModel";
import type { SyncApi, SyncOpInput, SyncRoomStateView } from "./syncApi";

/**
 * RoomSyncProvider - the one component mounted inside LiveKitRoom that owns the data channel,
 * the op log, room_state reconciliation and frame bytes (D-01, D-04, D-09, D-10, D-12).
 *
 * One incoming-data-packet listener is the one trust gate for everything arriving on the data
 * channel: every payload goes through `acceptSender` (identity from the listener's own
 * participant argument, never from the message) and `decodeEnvelope` before it ever reaches the
 * reducer. The backend is always the repair path - divergence is fixed by fetching `room_state`,
 * never by negotiating with a peer (D-04, D-09).
 *
 * Never logs message payloads, frame bytes or identities together with content (AGENTS.md: no
 * PII in logs).
 */

const REMOTE_OP_REFRESH_DEBOUNCE_MS = 1000;
const FROZEN_FRAME_POLL_MS = 15000;
const POINTER_FADE_TICK_MS = 100;
const OP_POST_RETRY_DELAYS_MS = [500, 1500, 3000];
const MAX_CACHED_FRAME_IMAGES = 3;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type PointerSource = { kind: "track" | "frame"; id: string };

export type SubmitOpResult = { ok: true; opId: string } | { ok: false; reason: "oversize" | "not-permitted" };

export type RoomSync = {
  ready: boolean;
  localIdentity: string;
  role: "worker" | "customer";
  room: RoomStatePayload | null;
  canAnnotate: boolean;
  opLog: OpLogState;
  shapesFor(frameId: string): Shape[];
  lastError: string | null;
  clearError(): void;
  submitOp(draft: OpDraft): SubmitOpResult;
  publishPointer(p: PointerPayload | null): void;
  pointersFor(source: PointerSource): Array<RemotePointer & { opacity: number }>;
  frameImageSrc(frameId: string): string;
  registerLocalFrameImage(frameId: string, blob: Blob): void;
  sendFrameBytes(frameId: string, bytes: Uint8Array): Promise<void>;
  refresh(): void;
  applyServerState(view: SyncRoomStateView): void;
  api: SyncApi;
};

const RoomSyncContext = createContext<RoomSync | null>(null);

export function useRoomSyncOptional(): RoomSync | null {
  return useContext(RoomSyncContext);
}

export function useRoomSync(): RoomSync {
  const ctx = useContext(RoomSyncContext);
  if (ctx === null) {
    throw new Error("useRoomSync must be used within a RoomSyncProvider");
  }
  return ctx;
}

/** HTTP error inspection, backed by axios's own type guard and the project's shared
 * handleAxiosError helper, so the validationMessages shape stays in sync with the rest of the
 * app instead of being duplicated here. */
function httpErrorStatus(err: unknown): number | null {
  if (!isAxiosError(err)) {
    return null;
  }
  return typeof err.response?.status === "number" ? err.response.status : null;
}

function httpValidationMessage(err: unknown, fallback: string): string {
  if (!isAxiosError(err)) {
    return fallback;
  }
  const { validationMessages } = handleAxiosError(err);
  const messages = validationMessages ? Object.values(validationMessages) : [];
  return messages.length > 0 ? messages.join(", ") : fallback;
}

export function RoomSyncProvider(p: { api: SyncApi; children: ReactNode }) {
  const room = useRoomContext();
  const connectionState = useConnectionState(room);

  const [state, dispatch] = useReducer(syncReducer, undefined, initialSyncState);
  const stateRef = useRef<SyncState>(state);
  stateRef.current = state;

  const apiRef = useRef(p.api);
  apiRef.current = p.api;

  const [ready, setReady] = useState(false);
  const [, setFrameImageVersion] = useState(0);

  const pointerThrottleRef = useRef(createPointerThrottle());
  const frameImagesRef = useRef<Map<string, string>>(new Map());
  const opQueueRef = useRef<Promise<void>>(Promise.resolve());
  const refreshInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const initialRefreshTriggeredRef = useRef(false);
  const remoteOpDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getRoomStateMutation = useMutation({
    mutationFn: (sinceSeq: number) => apiRef.current.getRoomState(sinceSeq),
  });
  const getRoomStateRef = useRef(getRoomStateMutation.mutateAsync);
  getRoomStateRef.current = getRoomStateMutation.mutateAsync;

  // The author's own POST of an op (D-09) - submitOp enqueues onto a serial promise chain so
  // authoring order stays seq order regardless of network timing.
  const appendOpMutation = useMutation({
    mutationFn: (input: SyncOpInput) => apiRef.current.appendOp(input),
  });
  const appendOpRef = useRef(appendOpMutation.mutateAsync);
  appendOpRef.current = appendOpMutation.mutateAsync;

  /** Every server view goes through roomStatePayloadSchema (which validates each op view through
   * opViewSchema as part of its `ops` array) before it is ever dispatched (D-03/D-09). */
  const applyServerView = useCallback((view: SyncRoomStateView) => {
    const result = roomStatePayloadSchema.safeParse(view);
    if (!result.success) {
      console.warn("[remote-support-sync] dropped an invalid room_state view");
      return;
    }
    const parsed = result.data;
    if (parsed.ops.length > 0) {
      dispatch({ type: "serverOps", ops: parsed.ops, nowMs: Date.now() });
    }
    dispatch({ type: "serverState", room: parsed });
  }, []);

  const runRefresh = useCallback(async () => {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }
    refreshInFlightRef.current = true;
    let succeededOnce = false;
    try {
      let more = true;
      while (more) {
        const since = stateRef.current.opLog.maxConfirmedSeq < 0 ? 0 : stateRef.current.opLog.maxConfirmedSeq;
        let view: SyncRoomStateView;
        try {
          view = await getRoomStateRef.current(since);
        } catch (err) {
          console.warn("[remote-support-sync] room_state refresh failed", err);
          break;
        }
        applyServerView(view);
        succeededOnce = true;
        more = view.hasMoreOps === true;
      }
    } finally {
      refreshInFlightRef.current = false;
      if (succeededOnce) {
        setReady(true);
      }
      if (refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void runRefresh();
      }
    }
  }, [applyServerView]);

  const runRefreshRef = useRef(runRefresh);
  runRefreshRef.current = runRefresh;

  const refresh = useCallback(() => {
    void runRefreshRef.current();
  }, []);

  const applyServerState = useCallback(
    (view: SyncRoomStateView) => {
      applyServerView(view);
    },
    [applyServerView],
  );

  // Trigger: first render after the connection is Connected.
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && !initialRefreshTriggeredRef.current) {
      initialRefreshTriggeredRef.current = true;
      void runRefreshRef.current();
    }
  }, [connectionState]);

  // Trigger: RoomEvent.Reconnected (D-10 reconnect restore).
  useEffect(() => {
    const onReconnected = () => {
      void runRefreshRef.current();
    };
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room]);

  // Trigger: a 15s poll while a frame is frozen (T-05-67 accepted risk: at most one fetch in
  // flight, debounced, only while frozen).
  const frozen = state.room?.activeFrame != null;
  useEffect(() => {
    if (!frozen) {
      return;
    }
    const id = setInterval(() => {
      void runRefreshRef.current();
    }, FROZEN_FRAME_POLL_MS);
    return () => clearInterval(id);
  }, [frozen]);

  // Trigger: fade animation while any pointer is tracked (state changes on dispatch already
  // re-render the tree; this tick only advances "now" so opacity keeps animating).
  const hasPointers = state.pointers.size > 0;
  useEffect(() => {
    if (!hasPointers) {
      return;
    }
    const id = setInterval(() => {
      dispatch({ type: "prunePointers", nowMs: Date.now() });
    }, POINTER_FADE_TICK_MS);
    return () => clearInterval(id);
  }, [hasPointers]);

  // The single data-channel trust gate: acceptSender before decodeEnvelope, for every topic.
  useEffect(() => {
    const onDataReceived = (payload: Uint8Array, participant?: { identity: string }, _kind?: unknown, topic?: string) => {
      const sender = participant?.identity ?? null;
      if (!acceptSender(topic, sender)) {
        console.debug(`[remote-support-sync] dropped data on topic ${String(topic)}: sender not accepted`);
        return;
      }
      const decoded = decodeEnvelope(topic, payload);
      if (!decoded.ok) {
        // decodeEnvelope already logged topic + reason.
        return;
      }
      const nowMs = Date.now();
      if (decoded.type === "room-state") {
        const incoming = decoded.payload as RoomStatePayload;
        dispatch({ type: "serverState", room: incoming });
        if (incoming.latestSeq > stateRef.current.opLog.maxConfirmedSeq) {
          void runRefreshRef.current();
        }
        return;
      }
      if (sender === null) {
        // Unreachable given acceptSender's rules for cl.op/cl.pointer; keeps TS narrowed.
        return;
      }
      if (decoded.type === "op") {
        dispatch({ type: "remoteOp", senderIdentity: sender, payload: decoded.payload as OpMessagePayload, nowMs });
        if (remoteOpDebounceTimerRef.current != null) {
          clearTimeout(remoteOpDebounceTimerRef.current);
        }
        remoteOpDebounceTimerRef.current = setTimeout(() => {
          remoteOpDebounceTimerRef.current = null;
          void runRefreshRef.current();
        }, REMOTE_OP_REFRESH_DEBOUNCE_MS);
        return;
      }
      if (decoded.type === "pointer") {
        dispatch({ type: "remotePointer", senderIdentity: sender, payload: decoded.payload as PointerPayload, nowMs });
        return;
      }
      if (decoded.type === "pointer-hide") {
        dispatch({ type: "remotePointerHide", senderIdentity: sender });
      }
    };

    room.on(RoomEvent.DataReceived, onDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, onDataReceived);
      if (remoteOpDebounceTimerRef.current != null) {
        clearTimeout(remoteOpDebounceTimerRef.current);
      }
    };
  }, [room]);

  // The durable frame image channel: cl.frame byte streams, accepted only from a worker or from
  // the currently-named freeze/active-frame source (T-05-64: size and mime checked before and
  // while reading, at most MAX_CACHED_FRAME_IMAGES blob URLs kept).
  useEffect(() => {
    room.registerByteStreamHandler(TOPICS.frame, (reader, participantInfo) => {
      const info = reader.info;
      if (info.mimeType !== "image/jpeg" || !UUID_PATTERN.test(info.id)) {
        return;
      }
      if (info.size !== undefined && info.size > MAX_FRAME_BYTES) {
        return;
      }
      const senderIdentity = participantInfo.identity;
      const currentRoom = stateRef.current.room;
      const isWorker = roleOfIdentity(senderIdentity) === "worker";
      const isNamedSource =
        currentRoom?.freezeRequest?.identity === senderIdentity ||
        currentRoom?.activeFrame?.sourceIdentity === senderIdentity;
      if (!isWorker && !isNamedSource) {
        return;
      }

      void (async () => {
        const chunks: Uint8Array[] = [];
        let total = 0;
        try {
          for await (const chunk of reader) {
            total += chunk.byteLength;
            if (total > MAX_FRAME_BYTES) {
              return;
            }
            chunks.push(chunk);
          }
        } catch (err) {
          console.warn("[remote-support-sync] frame byte stream read failed", err);
          return;
        }
        registerFrameImage(info.id, new Blob(chunks as BlobPart[], { type: "image/jpeg" }));
      })();
    });
    return () => {
      room.unregisterByteStreamHandler(TOPICS.frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registerFrameImage is stable (defined below with []).
  }, [room]);

  const registerFrameImage = useCallback((frameId: string, blob: Blob) => {
    const map = frameImagesRef.current;
    const existing = map.get(frameId);
    if (existing) {
      URL.revokeObjectURL(existing);
      map.delete(frameId);
    }
    map.set(frameId, URL.createObjectURL(blob));
    while (map.size > MAX_CACHED_FRAME_IMAGES) {
      const oldestKey = map.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      const oldestUrl = map.get(oldestKey);
      if (oldestUrl) {
        URL.revokeObjectURL(oldestUrl);
      }
      map.delete(oldestKey);
    }
    setFrameImageVersion((v) => v + 1);
  }, []);

  // Revoke every cached frame image blob URL on unmount.
  useEffect(() => {
    const map = frameImagesRef.current;
    return () => {
      for (const url of map.values()) {
        URL.revokeObjectURL(url);
      }
      map.clear();
    };
  }, []);

  const enqueueOpPost = useCallback((op: SyncOp) => {
    const input: SyncOpInput = {
      opId: op.opId,
      frameId: op.frameId,
      kind: op.kind,
      payload: JSON.stringify(op.payload),
      undoOf: op.undoOf,
      redoOf: op.redoOf,
    };

    const attempt = (retryDelaysMs: number[]): Promise<void> =>
      appendOpRef.current(input).then(
        (accepted) => {
          dispatch({ type: "localOpConfirmed", opId: op.opId, seq: accepted.seq });
        },
        (err: unknown) => {
          if (httpErrorStatus(err) === 422) {
            dispatch({
              type: "localOpRejected",
              opId: op.opId,
              message: httpValidationMessage(err, "The annotation was not accepted."),
            });
            return;
          }
          const [delay, ...rest] = retryDelaysMs;
          if (delay === undefined) {
            dispatch({ type: "localOpRejected", opId: op.opId, message: "Could not save the annotation." });
            void runRefreshRef.current();
            return;
          }
          return new Promise<void>((resolve) => setTimeout(resolve, delay)).then(() => attempt(rest));
        },
      );

    // Authoring order = seq order: each op's POST is chained after the previous one settles.
    opQueueRef.current = opQueueRef.current.then(() => attempt(OP_POST_RETRY_DELAYS_MS));
  }, []);

  const submitOp = useCallback(
    (draft: OpDraft): SubmitOpResult => {
      const currentRoom = stateRef.current.room;
      if (apiRef.current.role === "customer" && !(currentRoom?.customerMayAnnotate ?? false)) {
        return { ok: false, reason: "not-permitted" };
      }

      const opId = newUuid();
      const envelopePayload: OpMessagePayload = {
        opId,
        frameId: draft.frameId,
        kind: draft.kind,
        payload: draft.payload as unknown as Record<string, unknown>,
        undoOf: draft.undoOf,
        redoOf: draft.redoOf,
      };
      const bytes = encodeEnvelope(TOPICS.op, "op", envelopePayload);
      if (bytes === null) {
        return { ok: false, reason: "oversize" };
      }

      const op: SyncOp = {
        opId,
        frameId: draft.frameId,
        kind: draft.kind,
        payload: draft.payload,
        authorIdentity: room.localParticipant.identity,
        undoOf: draft.undoOf,
        redoOf: draft.redoOf,
        seq: null,
      };
      dispatch({ type: "localOp", op, nowMs: Date.now() });
      room.localParticipant.publishData(bytes, { reliable: true, topic: TOPICS.op }).catch((err) => {
        console.warn("[remote-support-sync] failed to publish an op on the data channel", err);
      });
      enqueueOpPost(op);
      return { ok: true, opId };
    },
    [room, enqueueOpPost],
  );

  const publishPointer = useCallback(
    (pointer: PointerPayload | null) => {
      if (pointer === null) {
        pointerThrottleRef.current.forceNext();
        const bytes = encodeEnvelope(TOPICS.pointer, "pointer-hide", {});
        if (bytes !== null) {
          room.localParticipant.publishData(bytes, { reliable: false, topic: TOPICS.pointer }).catch(() => {});
        }
        return;
      }
      if (!pointerThrottleRef.current.shouldSend(Date.now())) {
        return;
      }
      const bytes = encodeEnvelope(TOPICS.pointer, "pointer", pointer);
      if (bytes !== null) {
        room.localParticipant.publishData(bytes, { reliable: false, topic: TOPICS.pointer }).catch(() => {});
      }
    },
    [room],
  );

  const pointersFor = useCallback(
    (source: PointerSource) => visiblePointers(state.pointers, Date.now(), source),
    [state.pointers],
  );

  const shapesFor = useCallback((frameId: string) => renderFrame(state.opLog, frameId), [state.opLog]);

  const frameImageSrc = useCallback(
    (frameId: string) => frameImagesRef.current.get(frameId) ?? apiRef.current.frameImageUrl(frameId),
    [],
  );

  const sendFrameBytes = useCallback(
    async (frameId: string, bytes: Uint8Array) => {
      try {
        const writer = await room.localParticipant.streamBytes({
          topic: TOPICS.frame,
          mimeType: "image/jpeg",
          streamId: frameId,
          name: frameId,
          totalSize: bytes.byteLength,
        });
        await writer.write(bytes);
        await writer.close();
      } catch (err) {
        // Non-fatal: receivers still load the durable copy over HTTP (frameImageSrc's fallback).
        console.warn("[remote-support-sync] failed to send frame bytes on the data channel", err);
      }
    },
    [room],
  );

  const clearError = useCallback(() => {
    dispatch({ type: "clearError" });
  }, []);

  const canAnnotate = apiRef.current.role === "worker" || (state.room?.customerMayAnnotate ?? false);

  // Stable reference: rebuilt only when a member actually changes, so consumers whose effects
  // depend on the whole `sync` object (e.g. SyncStageOverlay's FrozenFrameCanvas) are not
  // invalidated by unrelated dispatches such as the pointer-fade tick (WR-01).
  const roomSync = useMemo<RoomSync>(
    () => ({
      ready,
      localIdentity: room.localParticipant.identity,
      role: p.api.role,
      room: state.room,
      canAnnotate,
      opLog: state.opLog,
      shapesFor,
      lastError: state.lastError,
      clearError,
      submitOp,
      publishPointer,
      pointersFor,
      frameImageSrc,
      registerLocalFrameImage: registerFrameImage,
      sendFrameBytes,
      refresh,
      applyServerState,
      api: p.api,
    }),
    [
      ready,
      room.localParticipant.identity,
      p.api,
      state.room,
      canAnnotate,
      state.opLog,
      shapesFor,
      state.lastError,
      clearError,
      submitOp,
      publishPointer,
      pointersFor,
      frameImageSrc,
      registerFrameImage,
      sendFrameBytes,
      refresh,
      applyServerState,
    ],
  );

  return <RoomSyncContext.Provider value={roomSync}>{p.children}</RoomSyncContext.Provider>;
}

export default RoomSyncProvider;
